import PropTypes from "prop-types";
import { useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

import {
  Box,
  Drawer,
  Stack,
  Avatar,
  Typography,
  ListItemButton,
  useTheme,
  useMediaQuery,
} from "@mui/material";

import { alpha } from "@mui/material/styles";

import Scrollbar from "../../components/scrollbar";
import NavConfig from "./config-navigation";
import { NAV } from "./config-layout";

export default function Nav({ openNav, onCloseNav }) {
  const navItems = NavConfig();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const restaurantLogo = useSelector(
    (state) => state.restaurantinfoReducer.infoData?.logo
  );
  const user = useSelector(
    (state) => state.userLoginReducer.userLoginData?.user
  );

  const { pathname } = useLocation();

  useEffect(() => {
    if (openNav) {
      onCloseNav();
    }
  }, [pathname]);

  const renderAccount = (
    <Box
      sx={{
        my: 3,
        mx: 2.5,
        py: 2,
        px: 2.5,
        display: "flex",
        alignItems: "center",
        borderRadius: 1.5,
        bgcolor: (theme) => alpha(theme.palette.grey[500], 0.12),
      }}
    >
      <Avatar src={user?.profileImage} alt={user?.name} />
      <Box sx={{ ml: 2 }}>
        <Typography variant="subtitle2" sx={{ textTransform: "capitalize" }}>
          {user?.name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {user?.role}
        </Typography>
      </Box>
    </Box>
  );

  const renderMenu = (
    <Stack component="nav" spacing={0.5} sx={{ px: 2 }}>
      {navItems.map((item) => (
        <ListItemButton
          key={item.title}
          component={NavLink}
          to={item.path}
          className={({ isActive }) => (isActive ? "active" : "")}
          sx={{
            minHeight: 44,
            borderRadius: 1,
            typography: "body2",
            color: "text.secondary",
            textTransform: "capitalize",
            fontWeight: 500,
            px: 2,
            pl: item.title === "Category" ? 4 : 2, 
            transition: "all 0.2s ease",
            "&.active": {
              color: "primary.main",
              fontWeight: 600,
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.16),
              },
              "& .MuiListItemIcon-root": {
                color: "primary.main",
              },
            },
            "&:hover": {
              bgcolor: (theme) => alpha(theme.palette.grey[500], 0.08),
            },
          }}
        >
          <Box component="span" sx={{ width: 24, height: 24, mr: 2 }}>
            {item.icon}
          </Box>
          <Box component="span">{item.title}</Box>
        </ListItemButton>
      ))}
    </Stack>
  );

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        "& .simplebar-content": {
          height: 1,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        component="img"
        src={restaurantLogo || "/assets/images/avatars/restrologo.png"}
        alt="Logo"
        sx={{
          width: 100,
          height: 40,
          mt: "24px",
          mx: "auto",
          objectFit: "contain",
          display:"flex",
          justifyContent:"center"
        }}
      />

      {renderAccount}
      {renderMenu}
      <Box sx={{ flexGrow: 1 }} />
    </Scrollbar>
  );

  return (
    <Box
      sx={{
        flexShrink: { lg: 0 },
        width: { lg: NAV.WIDTH },
      }}
    >
      {isDesktop ? (
        <Box
          sx={{
            height: 1,
            position: "fixed",
            width: NAV.WIDTH,
            borderRight: (theme) => `1px dashed ${theme.palette.divider}`,
          }}
        >
          {renderContent}
        </Box>
      ) : (
        <Drawer
          open={openNav}
          onClose={onCloseNav}
          PaperProps={{
            sx: { width: NAV.WIDTH },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </Box>
  );
}

Nav.propTypes = {
  openNav: PropTypes.bool,
  onCloseNav: PropTypes.func,
};
